import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
	Button,
	CheckboxField,
	Input,
	NumberInput,
	SelectField,
	Switch,
	FormField, FormItem, FormLabel, FormControl, FormMessage,
	RadioGroup,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { RadioOption } from './form-options'
import { CurrencyField } from '@/components/currency-field'
import { VatSummary } from '@/components/vat-summary'

import { SectionHeading } from './section-heading'

const RENT_UNIT_OPTIONS = [
	'PER_MONTH', 'PER_SQM_MONTH', 'PER_YEAR', 'PER_SQM_YEAR', 'PER_SEAT_MONTH',
] as const
const LEASE_KIND_OPTIONS = ['DIRECT', 'SUBLEASE'] as const
/**
 * Быстрые варианты, а не полный список: залог — величина, и ограничивать её
 * набором значило повторить в разметке ту же ошибку, что была в базе. Всё
 * остальное вводится числом рядом.
 */
const DEPOSIT_MONTHS_QUICK = [0, 1, 2, 3] as const

/*
 * `penaltyGraceDays` is an Int column, and the seven options offered here could
 * not express the fifteen days a landlord had actually agreed. The labels were
 * hardcoded Russian besides, so they never translated. It is a number; ask for a
 * number.
 */

const PERIODICITY_OPTIONS = [
	{ value: 'WEEKLY',    labelKey: 'properties.rentTerms.period.weekly' },
	{ value: 'MONTHLY',   labelKey: 'properties.rentTerms.period.monthly' },
	{ value: 'QUARTERLY', labelKey: 'properties.rentTerms.period.quarterly' },
	{ value: 'YEARLY',    labelKey: 'properties.rentTerms.period.yearly' },
] as const

interface Step3RentTermsProps {
	/**
	 * Вид объекта и тип жилья, когда форма их не хранит.
	 *
	 * В мастере это поля создаваемого объекта, и они лежат в схеме по праву. В
	 * диалоге условий аренды объект уже существует, туда они попали только чтобы
	 * этот шаг мог по ним ветвиться — то есть чужая модель в схеме формы.
	 */
	propertyType?: string
	housingType?:  string
}

export function Step3RentTerms({ propertyType: propertyTypeProp, housingType: housingTypeProp }: Step3RentTermsProps = {}) {
	const { t } = useTranslation()
	const form = useFormContext()

	const propertyType = propertyTypeProp ?? form.watch('type')
	// Coworking joins this as it is added — the terms block is shared, not
	// per type.
	const isCommercial =
		propertyType === 'OFFICE' ||
		propertyType === 'RETAIL' ||
		propertyType === 'WAREHOUSE'
	const housingType  = housingTypeProp ?? form.watch('housingType')
	const rentAmount   = form.watch('rentAmount')
	const vatIncluded  = form.watch('vatIncluded')

	// UI-only toggles for section enable/disable. Initial state is derived from
	// whether the DB-backed field is already set; on toggle off, the DB field
	// is cleared immediately in the handler so the form state stays truthy.
	const [depositEnabled, setDepositEnabled] = useState(() => form.getValues('depositAmount') !== undefined)
	const [penaltyEnabled, setPenaltyEnabled] = useState(() => form.getValues('penaltyPercent') !== undefined)
	/**
	 * «Есть комиссия» отдельно от её размера.
	 *
	 * Одно числовое поле не могло выразить оба ответа: «Да» записывало
	 * undefined, что рисовалось как «ничего не выбрано», и поле процента,
	 * открывавшееся только при значении больше нуля, не появлялось никогда —
	 * комиссию нельзя было ввести вообще. Ноль по-прежнему значит «нет».
	 */
	const [hasAgentCommission, setHasAgentCommission] = useState<boolean | undefined>(
		() => {
			const stored = form.getValues('agentCommissionPercent')
			return stored === undefined ? undefined : stored > 0
		},
	)

	return (
		<div className="flex flex-col gap-5 px-4 pt-2 pb-8">
			<section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.rentTerms.rentAmount')}</SectionHeading>
				<div className="flex gap-2 items-start">
					<div className="flex flex-col gap-2 flex-1 max-w-[22.4375rem]">
						<NumberInput min={0} name="rentAmount" label={t('properties.rentTerms.amount')} placeholder="50 000" />
						<div className="flex gap-2">
							<SelectField
								name="periodicity"
								className="flex-1"
								options={PERIODICITY_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
							/>
							<CurrencyField className="flex-1" showChangeLink />
						</div>
					</div>
					{/* The rate is derived from the owner's tax regime — there is no
					    percentage to type in, only whether it sits inside the price. */}
					<div className="flex-1">
						<VatSummary
							propertyType={propertyType}
							housingType={housingType}
							amount={rentAmount}
							included={vatIncluded ?? true}
							onIncludedChange={(next) => form.setValue('vatIncluded', next, { shouldDirty: true })}
						/>
					</div>
				</div>
			</section>

<section className="flex flex-col gap-5">
				<label className="flex items-center gap-4 py-4 cursor-pointer">
					<SectionHeading className="py-0">{t('properties.rentTerms.deposit')}</SectionHeading>
					<Switch
						checked={depositEnabled}
						onCheckedChange={(c) => {
							const on = c === true
							setDepositEnabled(on)
							if (!on) form.setValue('depositAmount', undefined)
						}}
						aria-label={t('properties.rentTerms.depositToggle')}
					/>
				</label>
				{depositEnabled && (
					<div className="flex gap-2 items-start max-w-[22.4375rem]">
						<NumberInput min={0} name="depositAmount" label={t('properties.rentTerms.amount')} placeholder="50 000" className="flex-1" />
						<CurrencyField label={t('properties.rentTerms.currency')} className="flex-1" />
					</div>
				)}
			</section>

<section className="flex flex-col gap-5">
				<label className="flex items-center gap-4 py-4 cursor-pointer">
					<SectionHeading className="py-0">{t('properties.rentTerms.penalty')}</SectionHeading>
					<Switch
						checked={penaltyEnabled}
						onCheckedChange={(c) => {
							const on = c === true
							setPenaltyEnabled(on)
							if (!on) {
								form.setValue('penaltyPercent', undefined)
								form.setValue('penaltyGraceDays', undefined)
							}
						}}
						aria-label={t('properties.rentTerms.penaltyToggle')}
					/>
				</label>
				{penaltyEnabled && (
					<div className="flex gap-2 items-start">
						<NumberInput min={0} name="penaltyPercent" label={t('properties.rentTerms.penaltyRate')} placeholder="0.1" step={0.01} className="w-44" />
						<FormField
							control={form.control}
							name="penaltyGraceDays"
							render={({ field }) => (
								<FormItem className="w-44">
									<FormLabel>{t('properties.rentTerms.gracePeriod')}</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											step={1}
											placeholder="5"
											suffix={t('common.days')}
											value={field.value ?? ''}
											onChange={(e) => {
												const raw = e.target.value
												field.onChange(raw === '' ? undefined : Number(raw))
												if (raw !== '' && !form.getValues('penaltyPercent')) {
													form.setValue('penaltyPercent', 0.1)
												}
											}}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				)}
			</section>
			{/* One monthly sum for everything step 4 does not break down per meter.
			    It belongs to the rent terms rather than to PropertyUtility: that
			    row answers who pays, this field how much, and neither expresses
			    the other. */}
			<section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.rentTerms.otherUtilities')}</SectionHeading>
				<NumberInput
					min={0}
					name="otherUtilitiesPayment"
					label={t('properties.rentTerms.otherUtilitiesPayment')}
					placeholder="3 000"
					className="w-[22.4375rem]"
				/>
			</section>
			{/* Commercial lease terms. A residential let names a deposit as a sum
			    and charges per period; a commercial one names months and may charge
			    per square metre per year, so the two sets do not overlap. */}
			{isCommercial && <section className="flex flex-col gap-5">
				<SectionHeading>{t('properties.rentTerms.commercial')}</SectionHeading>
				<div className="flex gap-2 items-start">
					<FormField
						control={form.control}
						name="rentUnit"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.rentTerms.rentUnit')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger size="md" width="full">
											<SelectValue placeholder="—" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{RENT_UNIT_OPTIONS.map((value) => (
											<SelectItem key={value} value={value}>
												{t(`properties.rentTerms.rentUnitOptions.${value}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="leaseKind"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.rentTerms.leaseKind')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger size="md" width="full">
											<SelectValue placeholder="—" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{LEASE_KIND_OPTIONS.map((value) => (
											<SelectItem key={value} value={value}>
												{t(`properties.rentTerms.leaseKindOptions.${value}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="flex gap-2 items-start">
					{/* Да/Нет, а потом процент. Пустое числовое поле не отличает
					    «комиссии нет» от «не заполнили», а Авито ждёт один из двух
					    ответов. Ноль и есть «нет» — отдельная колонка для этого не
					    нужна, а null остаётся честным «не указано». */}
					<FormField
						control={form.control}
						name="agentCommissionPercent"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('properties.rentTerms.agentCommission')}</FormLabel>
								<FormControl>
									<RadioGroup
										value={hasAgentCommission === undefined ? undefined : hasAgentCommission ? 'YES' : 'NO'}
										onValueChange={(v) => {
											const yes = v === 'YES'
											setHasAgentCommission(yes)
											// «Да» ждёт числа от пользователя, «нет» — это ноль.
											field.onChange(yes ? undefined : 0)
										}}
										name={field.name}
										className="flex flex-wrap gap-2"
									>
										<RadioOption value="YES" id="agent-commission-YES" label={t('common.yes')} />
										<RadioOption value="NO"  id="agent-commission-NO"  label={t('common.no')} />
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{hasAgentCommission && (
						<NumberInput name="agentCommissionPercent" label={t('properties.rentTerms.agentCommissionPercent')} placeholder="50" min={0} className="flex-1" />
					)}
					<NumberInput name="minLeaseMonths" label={t('properties.rentTerms.minLeaseMonths')} placeholder="11" min={1} className="flex-1" />
				</div>
				<FormField
					control={form.control}
					name="depositMonths"
					render={({ field }) => (
						<FormItem className="w-1/2">
							<FormLabel>{t('properties.rentTerms.depositMonths')}</FormLabel>
							<div className="flex flex-wrap items-center gap-2">
								{DEPOSIT_MONTHS_QUICK.map((value) => (
									<Button
										key={value}
										type="button"
										size="sm"
										variant={Number(field.value) === value ? 'default' : 'outline'}
										onClick={() => field.onChange(value)}
									>
										{value === 0
											? t('properties.rentTerms.depositMonthsNone')
											: t('properties.rentTerms.depositMonthsValue', { count: value })}
									</Button>
								))}
								<FormControl>
									<Input
										type="number"
										min={0}
										step={0.5}
										className="w-28"
										placeholder={t('properties.rentTerms.depositMonthsOther')}
										value={field.value ?? ''}
										onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
									/>
								</FormControl>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>
				<div className="flex flex-col gap-4">
					<CheckboxField name="rentHolidays" label={t('properties.rentTerms.rentHolidays')} labelClassName="font-[500] cursor-pointer" />
					<CheckboxField name="utilitiesIncluded" label={t('properties.rentTerms.utilitiesIncluded')} labelClassName="font-[500] cursor-pointer" />
					<CheckboxField name="operatingExpensesIncluded" label={t('properties.rentTerms.operatingExpensesIncluded')} labelClassName="font-[500] cursor-pointer" />
				</div>
			</section>}
		</div>
	)
}
